import styled, { css } from 'styled-components';
import { useResponsive } from '/imports/core/api/responsiveContext';
import { default as intlHook } from '/imports/core/api/useIntl';
import useTracking from '/imports/core/hooks/useTracking';
import Flex from '/imports/core/ui/atoms/Flex';
import Modal from '/imports/core/ui/atoms/Modal';
import { FEATURED_TEMPLATES_MESSAGES, TEMPLATES } from '/imports/generator/api/constants';
import { OnboardingSliderTemplate as Template } from '/imports/onboarding/ui/atoms';
import CloseIcon from '/imports/resume-options/ui/assest/CloseIcon';

const MY_LIST = ['recommanded_modal_point_1', 'recommanded_modal_point_2', 'recommanded_modal_point_3'];

const OnboardingTemplateRecommanded = ({ selectTemplate, open, handlePopupToogle }) => {
  const { isMobile } = useResponsive();
  const { trackEvent } = useTracking();
  const { t } = intlHook();
  const budapestTemplate = TEMPLATES.find((r) => r.id === 'budapest');

  const modalStyles = {
    modalContainer: {
      display: 'flex',
      backgroundColor: 'transparent',
    },
    modalBackdrop: {
      backgroundColor: '#8c98aa99',
    },
    modalBody: {
      maxWidth: '800px',
      background: '#fff',
      boxShadow: '0 16px 48px 0 rgba(29, 44, 67, 0.2)',
      margin: `${isMobile ? 'auto 5%' : 'auto'}`,
      padding: `${isMobile ? '39px 16px' : '51px 49px'}`,
      width: 'auto',
      height: 'auto',
      position: 'relative',
      overflow: 'auto',
      borderRadius: '16px',
      flexGrow: '0',
      maxHeight: '95vh',
    },
  };

  const handleAccept = () => {
    trackEvent('recommended_template_selected');
    selectTemplate('budapest', 'budapest', true);
  };

  const handleViewAll = () => {
    trackEvent('see_all_templates_clicked');
    handlePopupToogle(false);
  };

  if (!isMobile) {
    return null;
  }

  const ButtonGroup = ({ handleAccept, handleViewAll }) => {
    return (
      <Flex $direction="column" style={{ width: '100%', gap: 8 }}>
        <Button onClick={handleAccept}>
          {t('recommanded_template_select')} <br />
          <MySpan>{t('recommanded_expert_advice')}</MySpan>
        </Button>
        <Button onClick={handleViewAll} $outlined={true}>
          {t('recommanded_see_all_template')}
        </Button>
      </Flex>
    );
  };

  return (
    <Modal
      noClicker={true}
      styles={modalStyles}
      open={open}
      onClose={() => {
        handlePopupToogle(false);
      }}
      openStateBase
      animation="empty"
      timeout={0}
    >
      <CloseButtonWrapper onClick={() => handlePopupToogle(false)}>
        <CloseIcon fill="#8F8F8F" width={18} height={18} />
      </CloseButtonWrapper>
      <Wrapper $alignItems="center" $direction="column">
        <Title dangerouslySetInnerHTML={{ __html: t('recommanded_modal_title') }} />
        <Content $alignItems="center">
          <Flex $direction="column" style={{ gap: 32 }}>
            <List>
              {MY_LIST.map((item, index) => (
                <Item key={index}>{t(item)}</Item>
              ))}
            </List>
            <ButtonGroup handleAccept={handleAccept} handleViewAll={handleViewAll} />
          </Flex>
          <div>
            <Template
              width={isMobile ? 154 : 255}
              height={isMobile ? 230 : 372}
              featured={true}
              noName={true}
              message={FEATURED_TEMPLATES_MESSAGES[0]}
              key={`${budapestTemplate.image || budapestTemplate.id}`}
              {...budapestTemplate}
              onSelect={() => {}}
              onTemplateSelect={() => {}}
            />
          </div>
        </Content>
      </Wrapper>
    </Modal>
  );
};

export default OnboardingTemplateRecommanded;

const MySpan = styled.span`
  font-size: 12px !important;
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.family.websiteBold};
  font-size: 32px;
  ${(p) => p.theme.max('sm')`
      font-size: 20px;
      text-align: center;
 `}
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 99;
  cursor: pointer;
  right: 17.5px;
  top: 17.5px;
  ${(p) => p.theme.max('sm')`
      right: 14px;
      top: 14px%;
 `}
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 6px;
  border-radius: 3px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.family.websiteSemiBold};

  & > span {
    font-size: 12px;
  }

  background-color: #4c7cf7;
  color: white;
  border: 1px solid #4c7cf7;
  font-size: 20px;
  box-shadow: 0px 16px 32px 0px #4c7cf74d;

  ${(p) =>
    p.$outlined &&
    css`
      background-color: transparent;
      color: #3d300b;
      border: 1px solid #3d300b;
      font-size: 20px;
      box-shadow: none;
      color: #4c7cf7 !important;
      border: 1px solid #4c7cf7;
      box-shadow: none;
    `}

  ${(p) => p.theme.max('sm')`
    font-size: 16px;
    & > span {
    font-size: 10px;
    }
    ${
      p.$outlined &&
      css`
        font-size: 16px;
      `
    }
   `}
`;

const Wrapper = styled(Flex)`
  position: relative;
  gap: 40px;

  ${(p) => p.theme.max('sm')`
      gap:20px;
   `}
  @media (min-width: 968px) and (max-height: 700px) {
    gap: 0;
  }
`;

const Content = styled(Flex)`
  gap: 60px;

  ${(p) => p.theme.max('sm')`
    gap: 24px;
    flex-direction: column-reverse;
  `}
`;

const Item = styled.li`
  font-size: 20px;
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 16px;

  ${(p) => p.theme.max('sm')`
   font-size: 14px;
  `}
`;

const List = styled.ul`
  display: grid;
  margin: 0;
  padding-left: 24px;
  row-gap: 18px;

  ${(p) => p.theme.max('sm')`
    row-gap: 12px;
  `}
`;
